'use client'

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { logger } from '@/utils/logger';
import { User } from '@/types/user';
import { Tenant, UserRole } from '@/types/tenants';
import { updateBugsnagUser } from '@/services/bugsnag';
import { tenantStorage } from '@/utils/tenant-storage';
import { authStorage } from '@/utils/auth-storage';
import { activityTracker } from '@/utils/activity-tracker';

const ROLE_HIERARCHY: UserRole[] = [
  'SpearfishDevOpsRole',
  'GlobalAdminRole',
  'TenantAdminRole',
  'SpearfishSalesEmployeeRole',
  'User',
  'Guest'
];

// Session keep-alive configuration
const SESSION_CHECK_INTERVAL = 60 * 1000; // 60 seconds
const ACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes

interface AuthContextType {
  user: User | null;
  login: (credentials: { email: string; password: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isSwitchingTenant: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  getHighestRole: () => UserRole;
  isGlobalAdmin: () => boolean;
  isAuthenticated: () => boolean;
  tenants: Tenant[];
  activeTenantId: string | undefined;
  version: string;
  buildTime: string;
  setActiveTenantId: (tenantId: string | undefined) => void;
  loadSessionAndTenants: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // console.log('AuthProvider rendering');
  
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [version, setVersion] = useState<string>('dev');
  const [buildTime, setBuildTime] = useState<string>('unknown');
  const [activeTenantId, setActiveTenantId] = useState<string | undefined>();
  const router = useRouter();
  const lastSessionCheckRef = useRef<number>(0);
  const isPasswordResetPage = typeof window !== 'undefined' && window.location.pathname.includes('passwordreset');

  const loadSessionAndTenants = useCallback(async () => {
    // Skip loading session and tenants for password reset pages
    if (isPasswordResetPage) {
      setIsLoading(false);
      return;
    }

    try {
      setIsSwitchingTenant(true);
      const sessionResponse = await fetch('/api/auth/session', {
        credentials: 'include'
      });

      if (sessionResponse.ok) {
        const data = await sessionResponse.json();
        const userData = convertToUser(data);
        setUser(userData);
        setVersion(userData.version || 'dev');
        setBuildTime(userData.buildTime || 'unknown');
        setActiveTenantId(userData.activeTenantId);
        updateBugsnagUser({
          id: userData.id,
          email: userData.email,
          name: userData.name
        });
      } else {
        logger.error('Failed to load session', 'AuthProvider', `Status: ${sessionResponse.status}`);
      }

      const tenantsResponse = await fetch('/api/tenants', {
        credentials: 'include'
      });

      if (tenantsResponse.ok) {
        const tenantsData = await tenantsResponse.json();
        setTenants(tenantsData);
      } else {
        logger.error('Failed to load tenants', 'AuthProvider', `Status: ${tenantsResponse.status}`);
      }
    } catch (_error) {
      logger.error('Failed to load session and tenants', 'AuthProvider', _error);
    } finally {
      setIsSwitchingTenant(false);
      setIsLoading(false);
    }
  }, [isPasswordResetPage]);

  const handleSetActiveTenantId = useCallback((tenantId: string | undefined) => {
    setActiveTenantId(tenantId);
    // Update storage to notify other tabs
    tenantStorage.updateTenant(tenantId);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Logout failed');
      }
    } catch (_error) {
      logger.error('Logout error', 'AuthProvider', _error);
    } finally {
      // Always clear state and redirect, even if the API call fails
      setUser(null);
      setTenants([]);
      setActiveTenantId(undefined);
      updateBugsnagUser(null);
      // Notify other tabs about logout
      authStorage.updateAuthState(false);
      router.replace('/login');
    }
  }, [router]);

  // Session keep-alive function
  const checkSession = useCallback(async () => {
    if (!user) return; // Only check if user is authenticated

    const now = Date.now();

    // Only check if it's been at least SESSION_CHECK_INTERVAL since the last check
    if (now - lastSessionCheckRef.current < SESSION_CHECK_INTERVAL) return;

    // Only check if there has been activity within ACTIVITY_THRESHOLD
    if (!activityTracker.hasActivityWithin(ACTIVITY_THRESHOLD)) return;

    try {
      // Get time since last activity to send to server
      const timeSinceLastActivity = activityTracker.getTimeSinceLastActivity();

      // Update the last check timestamp
      lastSessionCheckRef.current = now;

      logger.info('Performing session keep-alive check', 'AuthProvider', {
        timeSinceLastActivity
      });

      // Call the session endpoint with time since last activity in seconds
      const timeSinceLastActivitySeconds = Math.floor(timeSinceLastActivity / 1000);
      const response = await fetch(`/api/auth/session?t=${timeSinceLastActivitySeconds}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        logger.warn('Session keep-alive check failed', 'AuthProvider', {
          status: response.status,
          statusText: response.statusText
        });

        // If session is invalid (401) or expired (403), trigger logout
        if (response.status === 401 || response.status === 403) {
          logger.info('Session expired or unauthorized, logging out', 'AuthProvider');
          await handleLogout();
        }
      } else {
        logger.info('Session keep-alive check successful', 'AuthProvider');

        // Optionally refresh user data if needed
        const sessionData = await response.json();
        if (sessionData && sessionData.userId !== user.id) {
          logger.info('User session data changed, refreshing', 'AuthProvider');
          await loadSessionAndTenants();
        }
      }
    } catch (_error) {
      logger.error('Error during session keep-alive check', 'AuthProvider', _error);

      // On network errors or other failures, only log but don't auto-logout
      // This prevents logout on temporary network issues
      if (_error instanceof TypeError && _error.message.includes('Failed to fetch')) {
        logger.warn('Network error during session check, will retry', 'AuthProvider');
      }
    }
  }, [user, handleLogout, loadSessionAndTenants]);

  // Set up session keep-alive interval
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Initialize activity tracker
    const cleanupActivityTracker = activityTracker.init();
    
    // Set up interval for session checks
    const intervalId = setInterval(checkSession, 10000); // Check every 10 seconds, but actual checks are throttled
    
    return () => {
      clearInterval(intervalId);
      if (cleanupActivityTracker) cleanupActivityTracker();
    };
  }, [checkSession]);

  useEffect(() => {
    // Skip for password reset pages
    if (isPasswordResetPage) {
      setIsLoading(false);
      return () => {};
    }

    loadSessionAndTenants();

    // Set up auth storage listener for cross-tab auth state sync
    const cleanup = authStorage.onAuthStateChange(async (update) => {
      // Only update if the change came from another tab
      if (update.isAuthenticated) {
        await loadSessionAndTenants();
        router.refresh();
      } else {
        setUser(null);
        setTenants([]);
        setActiveTenantId(undefined);
        updateBugsnagUser(null);
        router.replace('/login');
      }
    });

    return cleanup;
  }, [loadSessionAndTenants, router, isPasswordResetPage]);

  // Listen for session verified events from the callback page
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleSessionVerified = (_event: Event) => {
      logger.info('Received session verified event', 'AuthProvider');
      loadSessionAndTenants();
    };

    window.addEventListener('auth:session-verified', handleSessionVerified);
    
    return () => {
      window.removeEventListener('auth:session-verified', handleSessionVerified);
    };
  }, [loadSessionAndTenants, isPasswordResetPage]);

  // Handle tenant updates from other tabs
  useEffect(() => {
    const cleanup = tenantStorage.onTenantChange(async (update) => {
      // Only update if the change came from another tab
      if (update.tenantId !== activeTenantId) {
        await loadSessionAndTenants();
        router.refresh();
      }
    });

    return cleanup;
  }, [activeTenantId, loadSessionAndTenants, router]);

  const handleLogin = useCallback(async (credentials: { email: string; password: string }) => {
    logger.info('Attempting login', 'AuthProvider');
    setIsLoading(true);
    
    try {
      // Make sure we're calling our frontend API route, not the backend directly
      const loginUrl = '/api/auth/login?useCookies=true';
      logger.info('Login URL', 'AuthProvider', { url: loginUrl });
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        logger.error('Login failed', 'AuthProvider', `Status: ${response.status}`);
        setIsLoading(false);
        throw new Error('Login failed');
      }

      // Login successful, now fetch the session
      const sessionResponse = await fetch('/api/auth/session', {
        credentials: 'include'
      });
      
      if (!sessionResponse.ok) {
        throw new Error('Session fetch failed after login');
      }

      const sessionData = await sessionResponse.json();
      const userData = convertToUser(sessionData);

      setUser(userData);
      setVersion(userData.version || 'dev');
      setBuildTime(userData.buildTime || 'unknown');
      setActiveTenantId(userData.activeTenantId);
      updateBugsnagUser({
        id: userData.id,
        email: userData.email,
        name: userData.name
      });

      // Notify other tabs about successful login
      authStorage.updateAuthState(true);

      // Also fetch tenants
      const tenantsResponse = await fetch('/api/tenants', {
        credentials: 'include'
      });

      if (tenantsResponse.ok) {
        const tenantsData = await tenantsResponse.json();
        setTenants(tenantsData);
      }

      setIsLoading(false);
      return true;
    } catch (_error) {
      setIsLoading(false);
      if (_error instanceof Error) {
        logger.error('Login error', 'AuthProvider', _error.message);
      } else {
        logger.error('Login error', 'AuthProvider', 'Unknown error occurred');
      }
      return false;
    }
  }, []);

  const value: AuthContextType = {
    user,
    login: handleLogin,
    logout: handleLogout,
    isLoading,
    isSwitchingTenant,
    hasRole: useCallback((role: string) => user?.roles?.includes(role) ?? false, [user]),
    hasAnyRole: useCallback((roles: string[]) => roles.some(role => user?.roles?.includes(role)) ?? false, [user]),
    getHighestRole: useCallback((): UserRole => {
      if (!user?.roles?.length) return 'Guest';
      return ROLE_HIERARCHY.find(role => user.roles.includes(role)) || 'Guest';
    }, [user?.roles]),
    isGlobalAdmin: useCallback((): boolean => {
      return user?.roles?.some(role => ['GlobalAdminRole', 'SpearfishDevOpsRole'].includes(role)) ?? false;
    }, [user?.roles]),
    isAuthenticated: useCallback(() => !!user, [user]),
    tenants,
    activeTenantId,
    version,
    buildTime,
    setActiveTenantId: handleSetActiveTenantId,
    loadSessionAndTenants,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function convertToUser(data: any): User {
  return {
    id: data.userId,
    email: data.email,
    name: data.name,
    roles: data.roles || [],
    status: 'active', // Assuming logged in users are active
    createdAt: new Date().toISOString(), // We don't have this from session, using current time
    tenantId: data.tenantId?.toString() || '',
    tenantName: data.tenantName || '',
    tenantMemberships: data.tenantMemberships?.split(',').map((id: string) => id.trim()) || [],
    activeTenantId: data.tenantId?.toString(),
    version: data.version || 'dev',
    buildTime: data.buildTime || 'unknown',
  };
}
